import {
  WhmBox,
  WhmChip,
  WhmFormHelperText,
  WhmLoadingButton,
  WhmStack,
  whmTheme,
  WhmTypography,
} from "@securitize/reactjs-whm";
import {
  I18nContext,
  translationKeys,
} from "../../../../../contexts/I18nContext";
import { Translate } from "../../../../Translation/Translate";
import React, { useContext } from "react";
import { Formik, FormikContextType, FormikProps } from "formik";
import * as Yup from "yup";
import {
  hasLowercase,
  hasMinLength,
  hasNumber,
  hasUppercase,
} from "./passwordUtils";
import TranslationKeys from "../../../../../contexts/I18nContext/translationKeys";
import HiddenField from "../../../../shared/HiddenField";

interface SettingsChangePasswordLegacyProps {
  changePassword: {
    isError: boolean;
    isLoading: boolean;
    onChangePassword: (currentPassword?: string, newPassword?: string) => void;
  };
}

export type ChangePasswordFormType = {
  currentPassword: string;
  newPassword: string;
};

const NEW_PASSWORD_MIN_LENGTH = 8;

const useValidationSchema = () => {
  const { getTranslation } = useContext(I18nContext);

  return Yup.object({
    currentPassword: Yup.string().required(
      getTranslation(
        translationKeys.PROFILE_SETTINGS_SECURITY_CARD_PASSWORD_REQUIRED_FIELD,
      ),
    ),
    newPassword: Yup.string()
      .required(
        getTranslation(
          translationKeys.PROFILE_SETTINGS_SECURITY_CARD_PASSWORD_REQUIRED_FIELD,
        ),
      )
      .min(
        NEW_PASSWORD_MIN_LENGTH,
        getTranslation(
          translationKeys.PROFILE_SETTINGS_SECURITY_CARD_PASSWORD_REQUIREMENTS_NOT_MET_MESSAGE,
        ),
      )
      .test(
        "has-lowercase",
        getTranslation(
          translationKeys.PROFILE_SETTINGS_SECURITY_CARD_PASSWORD_REQUIREMENTS_NOT_MET_MESSAGE,
        ),
        (value) => hasLowercase(value),
      )
      .test(
        "has-uppercase",
        getTranslation(
          translationKeys.PROFILE_SETTINGS_SECURITY_CARD_PASSWORD_REQUIREMENTS_NOT_MET_MESSAGE,
        ),
        (value) => hasUppercase(value),
      )
      .test(
        "has-number",
        getTranslation(
          translationKeys.PROFILE_SETTINGS_SECURITY_CARD_PASSWORD_REQUIREMENTS_NOT_MET_MESSAGE,
        ),
        (value) => hasNumber(value),
      ),
  });
};

export const SettingsChangePasswordLegacy = ({
  changePassword,
}: SettingsChangePasswordLegacyProps) => {
  const { getTranslation } = useContext(I18nContext);
  const validationSchema = useValidationSchema();
  const [showCurrentPassword, setShowCurrentPassword] = React.useState(false);
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(false);

  const handlePasswordChange = async (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    formik: FormikProps<ChangePasswordFormType>,
    field: keyof ChangePasswordFormType,
  ) => {
    const { value } = e.target;

    setIsEditing(true);

    await formik.setFieldValue(field, value);
    formik.handleChange(e);
  };

  const handleNewPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    formik: FormikProps<ChangePasswordFormType>,
  ) => {
    return handlePasswordChange(e, formik, "newPassword");
  };

  const handleCurrentPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    formik: FormikProps<ChangePasswordFormType>,
  ) => {
    return handlePasswordChange(e, formik, "currentPassword");
  };

  const getIsSubmitButtonDisabled = (
    formik: FormikContextType<ChangePasswordFormType>,
  ) => {
    return (
      (formik.touched.currentPassword || formik.touched.newPassword) &&
      (Boolean(formik.errors.newPassword) ||
        Boolean(formik.errors.currentPassword))
    );
  };

  const onFormSubmit = (values: ChangePasswordFormType) => {
    setIsEditing(false);
    changePassword.onChangePassword(values.currentPassword, values.newPassword);
    return;
  };

  const getChipColor = (
    validationFunction: (password: string) => boolean,
    formik: FormikContextType<ChangePasswordFormType>,
  ) => {
    if (validationFunction(formik.values.newPassword)) {
      return "success";
    }
    if (formik.values.newPassword && formik.touched.newPassword) {
      return "error";
    }
    return "default";
  };

  const getUpperCaseChipColor = (
    formik: FormikContextType<ChangePasswordFormType>,
  ) => {
    return getChipColor(hasUppercase, formik);
  };

  const getLowerCaseChipColor = (
    formik: FormikContextType<ChangePasswordFormType>,
  ) => {
    return getChipColor(hasLowercase, formik);
  };

  const getNumberChipColor = (
    formik: FormikContextType<ChangePasswordFormType>,
  ) => {
    return getChipColor(hasNumber, formik);
  };

  const getMinLengthChipColor = (
    formik: FormikContextType<ChangePasswordFormType>,
  ) => {
    return getChipColor(
      (password) => hasMinLength(password, NEW_PASSWORD_MIN_LENGTH),
      formik,
    );
  };

  const shouldShowNetworkCallError =
    changePassword.isError && !isEditing && !changePassword.isLoading;

  return (
    <WhmStack>
      <WhmStack sx={{ paddingBottom: "8px", marginBottom: "12px" }}>
        <WhmTypography variant="body2" color={whmTheme.palette.text.primary}>
          <Translate
            translationKey={
              translationKeys.PROFILE_SETTINGS_SECURITY_CARD_PASSWORD_TITLE
            }
          />
        </WhmTypography>
        <WhmTypography variant="body2" color={whmTheme.palette.text.secondary}>
          <Translate
            translationKey={
              translationKeys.PROFILE_SETTINGS_SECURITY_CARD_PASSWORD_SUBTITLE
            }
          />
        </WhmTypography>
      </WhmStack>

      <Formik<ChangePasswordFormType>
        initialValues={{
          currentPassword: "",
          newPassword: "",
        }}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          onFormSubmit(values);
        }}
      >
        {(formik) => {
          return (
            <WhmStack>
              {shouldShowNetworkCallError && (
                <WhmFormHelperText
                  error
                  sx={{ marginBottom: "16px !important" }}
                >
                  <Translate
                    translationKey={
                      TranslationKeys.PROFILE_SETTINGS_SECURITY_CARD_PASSWORD_GENERAL_ERROR_MESSAGE
                    }
                  />
                </WhmFormHelperText>
              )}

              <HiddenField<ChangePasswordFormType>
                id="change-password-current-password"
                label={
                  <Translate
                    translationKey={
                      TranslationKeys.PROFILE_SETTINGS_SECURITY_CARD_CURRENT_PASSWORD_LABEL
                    }
                  />
                }
                helperText={
                  formik.touched.currentPassword &&
                  formik.errors.currentPassword
                }
                field="currentPassword"
                showField={showCurrentPassword}
                setShowField={setShowCurrentPassword}
                handleFieldChange={handleCurrentPasswordChange}
                sx={{ marginBottom: "16px" }}
              />

              <HiddenField<ChangePasswordFormType>
                id="change-password-new-password"
                label={
                  <Translate
                    translationKey={
                      TranslationKeys.PROFILE_SETTINGS_SECURITY_CARD_NEW_PASSWORD_LABEL
                    }
                  />
                }
                field="newPassword"
                helperText={
                  formik.touched.newPassword && formik.errors.newPassword
                }
                showField={showNewPassword}
                setShowField={setShowNewPassword}
                handleFieldChange={handleNewPasswordChange}
                sx={{ marginBottom: "16px" }}
              />

              <WhmBox
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "12px",
                  marginBottom: "12px",
                  flexWrap: "wrap",
                }}
              >
                <WhmChip
                  variant={"filled"}
                  size={"small"}
                  label={getTranslation(
                    translationKeys.PROFILE_SETTINGS_SECURITY_CARD_PASSWORD_REQUIREMENTS_UPPERCASE,
                  )}
                  color={getUpperCaseChipColor(formik)}
                />
                <WhmChip
                  variant={"filled"}
                  size={"small"}
                  label={getTranslation(
                    translationKeys.PROFILE_SETTINGS_SECURITY_CARD_PASSWORD_REQUIREMENTS_LOWERCASE,
                  )}
                  color={getLowerCaseChipColor(formik)}
                />
                <WhmChip
                  variant={"filled"}
                  size={"small"}
                  label={getTranslation(
                    translationKeys.PROFILE_SETTINGS_SECURITY_CARD_PASSWORD_REQUIREMENTS_NUMBER,
                  )}
                  color={getNumberChipColor(formik)}
                />
                <WhmChip
                  variant={"filled"}
                  size={"small"}
                  label={getTranslation(
                    translationKeys.PROFILE_SETTINGS_SECURITY_CARD_PASSWORD_REQUIREMENTS_LENGTH,
                  )}
                  color={getMinLengthChipColor(formik)}
                />
              </WhmBox>

              <WhmLoadingButton
                variant={"outlined"}
                size={"medium"}
                color={"primary"}
                loadingPosition="start"
                loading={changePassword.isLoading}
                disabled={getIsSubmitButtonDisabled(formik)}
                sx={{
                  width: "fit-content",
                  minWidth: "fit-content !important",
                  svg: {
                    display: "block !important",
                  },
                  alignSelf: { xs: "start", xl: "end" },
                }}
                onClick={() => void formik.submitForm()}
              >
                {changePassword.isLoading ? (
                  <Translate
                    translationKey={
                      TranslationKeys.PROFILE_SETTINGS_SECURITY_CARD_PASSWORD_CTA_LOADING
                    }
                  />
                ) : (
                  <Translate
                    translationKey={
                      TranslationKeys.PROFILE_SETTINGS_SECURITY_CARD_PASSWORD_CTA
                    }
                  />
                )}
              </WhmLoadingButton>
            </WhmStack>
          );
        }}
      </Formik>
    </WhmStack>
  );
};
